import React from "react";
import PT from "prop-types";
import Layout from '../../../components/layout'
import SEO from "../../../components/seo"
import classnames from "classnames";
import styles from "./styles.module.scss"

const description = `
Words counter from your text. Words and characters histogram calculator.
Interactive free online words counter. Try it now! 
`

const splitRegex = /[\s.?!:,();]+/;

const roundNumber = (n) => Math.round(n * 100, 2) / 100;

class CharsHistogram extends React.PureComponent {
  
  static propTypes = {
    text: PT.string.isRequired,
    countWhitespaces: PT.bool,
  }

  static defaultProps = {
    countWhitespaces: false,
  }

  getHistogram = () => {
    const { text, countWhitespaces } = this.props;
    let histogram = text
      .split('')
      .filter(ch => !ch.match(splitRegex))
      .map(ch => ch.toLowerCase())
      .reduce((acc, char) => {
          if (!countWhitespaces && char === " ") {
            return acc;
          }
          acc[char] = char in acc ? acc[char] + 1 : 1;
          return acc;
      }, {});
  
    return Object
      .entries(histogram)
      .sort((a, b) => a[1] < b[1] ? 1 : -1)
  }

  render() {
    
    return (
      <div className={styles.charsHistogram}>
        <h2>Characters histogram</h2>
        <table>
          <tbody>
          {
            this.getHistogram().map(entry => (
              <tr key={entry[0]}>
                <td>{entry[0]}</td>
                <td>{entry[1]}</td>
              </tr>
            ))
          }
          </tbody>
        </table>
      </div>
    )
  }
}

const WordsStats = ({
  words = 0,
  charCount = 0, 
  charsWithoutSpaces = 0,
  spaces = 0,
  meanLength = 0,
  averageLength = 0,
}) => {
  return (
    <div className={styles.fullWidth}>
      <h2>Stats</h2>
      <table>
        <tbody>
          <tr><td>Words</td><td>{words}</td></tr>
          <tr><td>Characters (With Spaces)</td><td>{charCount}</td></tr>
          <tr><td>Characters (Without Spaces)</td><td>{charsWithoutSpaces}</td></tr>
          <tr><td>Spaces</td><td>{spaces}</td></tr>
          <tr><td>Average word length</td><td>{roundNumber(averageLength)}</td></tr>
          <tr><td>Mean word length</td><td>{roundNumber(meanLength)}</td></tr>
        </tbody>
      </table>
    </div>
  )
}

const WordRow = ({word, count}) => (
  <tr>
    <td>{word}</td>
    <td>{count}</td>
  </tr>
);

class WordsHistogram extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      howMuchToShow: 50,
      showAll: false,
      error: null,
    };
  }

  static propTypes = {
    words: PT.arrayOf(PT.string).isRequired,
  }

  hideAll = () => this.setState({ showAll: false });
  showAll = () => this.setState({ showAll: true });
  toggleShowAll = () => this.setState({ showAll: !this.state.showAll });
  howMuchChange = (e) => {
    if (e.target.value === "") {
      e.target.value = 0;
    }
    const tryParse = parseInt(e.target.value)
    if (isNaN(tryParse)) {
      this.setState({ error: 'Set positive natural number (for example 50)'})
    } else {
      this.setState({
        error: null,
        howMuchToShow: tryParse,
      })
    }
  };

  computeWordsHistogram = () => {
    const words = this.props.words;
    const lowercasedWords = words.map(w => w.toLowerCase())
    const wordsCounter = lowercasedWords.reduce((acc, word) => {
      acc[word] = (word in acc) ? acc[word] + 1 : 1;
      return acc;
    }, {});
    
    const sortedWordsHistogram = 
      Object.entries(wordsCounter)
      .sort((entryA, entryB) => entryA[1] < entryB[1] ? 1 : -1)

    return sortedWordsHistogram;
  }

  render() {
    return (
      <div className={styles.wordHistogram}>
        <h2>Words Histogram</h2>
        <div>ShowAll 
          <input 
            type="checkbox" 
            className="margin-left"
            checked={this.state.showAll}
            onChange={this.toggleShowAll}
          />
        </div>
        {!this.state.showAll && (
          <div>How much words to show? 
           <input
              className="margin-left"
              type="number" 
              onChange={this.howMuchChange}
              value={this.state.howMuchToShow === 0 ? "" : this.state.howMuchToShow}
           />
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th><b>Word</b></th><th><b>Count</b></th>
            </tr>
          </thead>
          <tbody>
          {
              this.computeWordsHistogram()
              .slice(0, this.state.howMuchToShow)
              .map(([word, count]) => <WordRow word={word} count={count} key={word} />)
          }
          </tbody>
        </table>
      </div>
      )
    };
} 

class CountWordsPage extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      text: "",
      words: [],
    }
  }

  computeWords = text => 
    text.split(splitRegex).filter(word => word !== "" && word !== " ");
  
  computeAverageLength = () => {
    const words = this.state.words;
    let sum = 0;
    words.forEach(word => sum += word.length);
    return words.length === 0 ? 0 : sum / words.length;
  }
  
  computeMeanLength = () => {
    let words = this.state.words.slice();
    if (words.length === 0) {
      return 0;
    }
    words = words.sort((a,b) => a.length < b.length ? -1 : 1)
    return words[Math.floor(words.length / 2)].length;
  }

  timeoutFn = (text) => {
    this.setState({
        words: this.computeWords(text),
    });
  };

  textAreaChangeHandler = (e) => {
    const { value } = e.target;

    // delete previous processor
    clearTimeout(this.delayedProcessor);

    // create new
    this.delayedProcessor = setTimeout(() => this.timeoutFn(value), 2);

    this.setState({
      text: value,
    })
  };

  charsWithoutSpaces = (text) => {
    const newText = text.replace(/\s/g, '')
    return newText.length
  }

  render() {
    const charsWithoutSpaces = this.charsWithoutSpaces(this.state.text)
    return (
      <Layout withFooter={false}>
        <div className={classnames(styles.container, styles.childrenWithShadow)}>
          <SEO
            title={"Words Counter"}
            description={description}
            keywords={["words", "counter", "histogram"]}
          />
          <div className={styles.fullWidth}>
            <div>Paste Your Text Below:</div>
            <textarea className={styles.textarea} onChange={this.textAreaChangeHandler} />
          </div>
          <div className={styles.fullWidth}><b>Words:</b> {this.state.words.length}</div>
          <WordsStats
            words={this.state.words.length}
            charCount={this.state.text.length}
            charsWithoutSpaces={charsWithoutSpaces}
            spaces={this.state.text.length - charsWithoutSpaces}
            averageLength={this.computeAverageLength()}
            meanLength={this.computeMeanLength()}
          />
          <WordsHistogram words={this.state.words} />
          <CharsHistogram text={this.state.text} />
        </div>
      </Layout>
    );
  }
 }

export default CountWordsPage;
